import { normaliseOptionValue, type BodyMeasurements, type OptionValue, type PatternProject, type PatternSpecification } from "@tailor-frame/engine";
import { defaultsFor, linkedMeasurements, type BodyMeasurementSet } from "@tailor-frame/patterns";

/**
 * Each pattern's style options and pattern measurements, by pattern id, so a
 * pattern reopens as it was left and a print set can draft patterns that are
 * not open. Body measurements are kept apart and win over anything saved here.
 */
const settingsStorageKey = "tailor-frame:pattern-settings";

export interface PatternSettings { options: Record<string, OptionValue>; measurements: Record<string, number> }

function loadAll(): Record<string, PatternSettings> {
  try { return JSON.parse(localStorage.getItem(settingsStorageKey) ?? "{}") as Record<string, PatternSettings>; } catch { return {}; }
}

export function savePatternSettings(project: PatternProject) {
  const next = { ...loadAll(), [project.specification.id]: { options: project.options, measurements: project.measurements } };
  try { localStorage.setItem(settingsStorageKey, JSON.stringify(next)); } catch { /* storage unavailable: keep in memory only */ }
}

/** The body a pattern is drafted for: the shared client measurements, as saved by the measurements step. */
export interface DraftBody { madeFor: string; values: BodyMeasurementSet; figure?: BodyMeasurements }

/**
 * A pattern's project: its defaults, then its saved options and measurements,
 * then every measurement the body covers filled from the body.
 */
export function projectForPattern(specification: PatternSpecification, body: DraftBody, includeInlay: boolean): PatternProject {
  const next = defaultsFor(specification);
  const saved = loadAll()[specification.pattern.id];
  if (saved) {
    // A saved value must still suit the pattern: an option it still offers, a measurement it still reads.
    for (const [id, definition] of Object.entries(specification.options)) if (saved.options?.[id] !== undefined) next.options[id] = normaliseOptionValue(definition, saved.options[id]);
    for (const id of Object.keys(next.measurements)) if (typeof saved.measurements?.[id] === "number") next.measurements[id] = saved.measurements[id];
  }
  next.madeFor = body.madeFor;
  next.measurements = { ...next.measurements, ...linkedMeasurements(specification, body.values) };
  if (body.figure) next.bodyMeasurements = { ...body.figure };
  next.includeInlay = includeInlay;
  return next;
}
